<script lang="ts" setup>
import Container from '@/components/Container.vue';
import { LANDING_CONSULTATION_PRICE } from '@/landing/landingConsultationPrice.js';
import { formatMoney } from '@nzyme/money/formatMoney.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';

import * as l from './BenefitsSection.loc.js';
import StartConsultationButton from './StartConsultationButton.vue';

const t = useTranslate();
</script>

<template>
  <Container
    id="benefits"
    tag="section"
    :class="css.benefitsSection"
  >
    <div :class="css.sectionTitle">
      <h2>{{ t(l.benefitsTitle) }}</h2>
    </div>
    <ul :class="[css.benefitsItems, css.borderPatientGradientContainer, css.animated]">
      <li :class="[css.benefitItem, css.borderPatientGradientItem, css.animated]">
        <div :class="css.icon">
          <span class="material-symbols-outlined">acute</span>
        </div>
        <h3>{{ t(l.benefitsNoPaperworkTitle) }}</h3>
        <p>{{ t(l.benefitsNoPaperworkDescription) }}</p>
      </li>
      <li :class="[css.benefitItem, css.borderPatientGradientItem, css.animated]">
        <div :class="css.icon">
          <span class="material-symbols-outlined">sweep</span>
        </div>
        <h3>{{ t(l.benefitsConsultFromHomeTitle) }}</h3>
        <p>{{ t(l.benefitsConsultFromHomeDescription) }}</p>
      </li>
      <li :class="[css.benefitItem, css.borderPatientGradientItem, css.animated]">
        <div :class="css.icon">
          <span class="material-symbols-outlined">work_history</span>
        </div>
        <h3>{{ t(l.benefitsSameDayHelpTitle) }}</h3>
        <p>{{ t(l.benefitsSameDayHelpDescription) }}</p>
      </li>
      <li :class="[css.benefitItem, css.borderPatientGradientItem, css.animated]">
        <div :class="css.icon">
          <span class="material-symbols-outlined">prescriptions</span>
        </div>
        <h3>{{ t(l.benefitsNoHiddenCostsTitle) }}</h3>
        <p>
          {{
            t(l.benefitsNoHiddenCostsDescription, {
              price: formatMoney(LANDING_CONSULTATION_PRICE, { withCurrency: 'code' }),
            })
          }}
        </p>
      </li>
    </ul>
    <div :class="css.cta">
      <h3>{{ t(l.benefitsCtaHeading) }}</h3>
      <p>{{ t(l.benefitsCtaDescription) }}</p>
      <StartConsultationButton />
    </div>
  </Container>
</template>

<style lang="scss" module="css">
.benefitsSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-medium);
  padding-block: var(--space-large);

  // Flow spacing for child elements
  > * + * {
    margin-block-start: clamp(0.75rem, 1.5vw, 1.25rem);
  }
}

.sectionTitle {
  display: flex;
  flex-direction: column;
  gap: var(--space-xxxsmall);
  text-align: center;
  margin-inline: auto;
  max-width: var(--max-width-medium);

  h2 {
    margin: 0;
    font-size: var(--font-size-heading-2);
    font-weight: $font-weight-600;
    line-height: $line-height-heading-2;
    letter-spacing: $letter-spacing-heading-2;
  }
}

.benefitsItems {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(1, 1fr);

  @media screen and (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.benefitItem {
  padding: var(--space-xsmall);

  h3 {
    font-size: var(--font-size-text-large);
    margin: 1rem 0 1rem 0;
    font-weight: $font-weight-600;
    letter-spacing: $letter-spacing-heading-3;
    -webkit-font-smoothing: antialiased;
  }

  p {
    margin: 0;
    color: $color-gray-700;
  }
}

.icon {
  span {
    display: block;
    font-size: var(--font-size-heading-3);
    font-variation-settings:
      'FILL' 0,
      'wght' 350,
      'GRAD' 0,
      'opsz' 40;
  }
}

.borderPatientGradientContainer {
  border-style: solid;
  border-width: 1px;
  border-color: $color-patient-200;
  border-image: linear-gradient(135deg, $color-patient-200 30%, $color-doctor-200 70%);
  border-image-slice: 1;
  border-image-width: 1;
  border-image-outset: 0;
}

.borderPatientGradientItem {
  border-style: solid;
  border-color: $color-patient-200;
  border-image: linear-gradient(135deg, $color-patient-200 30%, $color-doctor-200 70%);
  border-image-slice: 1;
  border-image-width: 1;
  border-image-outset: 0;

  // Mobile: bottom border only
  border-width: 0 0 1px 0;

  &:last-of-type {
    border-width: 0;
  }

  @media screen and (min-width: 480px) {
    border-width: 0 1px 0 0;

    &:first-of-type {
      border-width: 0 1px 1px 0;
    }

    &:nth-of-type(odd) {
      border-width: 0 1px 1px 0;
    }

    &:nth-of-type(n + 3) {
      border-width: 0 1px 0 0;
    }

    &:last-of-type {
      border-width: 0;
    }
  }

  @media screen and (min-width: 1024px) {
    &:nth-of-type(n + 1) {
      border-width: 0 1px 0 0;
    }

    &:last-of-type {
      border-width: 0;
    }
  }
}

.animated {
  animation: gradient-rotate 10s $transition-ease-out-quad infinite;
}

.cta {
  display: flex;
  flex-direction: column;
  gap: var(--space-xxsmall);
  text-align: center;
  max-width: var(--max-width-medium);
  margin-inline: auto;
  align-items: center;

  h3 {
    margin: 0;
    font-size: var(--font-size-heading-4);
    font-weight: $font-weight-600;
    line-height: $line-height-heading-4;
    letter-spacing: $letter-spacing-heading-4;
  }

  p {
    margin: 0;
    color: $color-gray-700;
  }
}
</style>
